/**
 * Sponsor-side approval queue (NOS-171): approve (writes the ledger entry
 * atomically server-side) or deny with a required note.
 */
import { useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useDisbursementRequests, type DisbursementRequest } from "@/hooks/useDisbursementRequests"
import { formatCurrencyCents } from "@/lib/sponsor"

export function DisbursementQueue({
  sponsorshipId,
  canDecide,
  onDecided,
}: {
  sponsorshipId: string
  canDecide: boolean
  onDecided: () => void
}) {
  const requests = useDisbursementRequests({ sponsorshipId })
  const [denyTarget, setDenyTarget] = useState<DisbursementRequest | null>(null)
  const [denyNote, setDenyNote] = useState("")
  const [busy, setBusy] = useState(false)

  const pending = requests.requests.filter((r) => r.status === "pending")
  if (pending.length === 0) return null

  const handleApprove = async (r: DisbursementRequest) => {
    setBusy(true)
    const { error } = await requests.decide(r.id, true)
    setBusy(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success(`Approved — ${formatCurrencyCents(r.amount)} disbursed`)
    onDecided()
  }

  const handleDeny = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!denyTarget) return
    if (!denyNote.trim()) return
    setBusy(true)
    const { error } = await requests.decide(denyTarget.id, false, denyNote.trim())
    setBusy(false)
    if (error) {
      toast.error(error.message)
      return
    }
    toast.success("Request denied")
    setDenyTarget(null)
    setDenyNote("")
    onDecided()
  }

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
      <h2 className="font-semibold">
        Funds requests
        <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
          {pending.length} pending
        </span>
      </h2>
      <ul className="mt-3 divide-y rounded-md border bg-white">
        {pending.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
            <span className="font-medium tabular-nums">{formatCurrencyCents(r.amount)}</span>
            <span className="min-w-0 flex-1 truncate">{r.purpose}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(r.created_at).toLocaleDateString()}
            </span>
            {canDecide && (
              <span className="flex gap-1">
                <Button size="sm" onClick={() => handleApprove(r)} disabled={busy}>
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setDenyTarget(r)}
                  disabled={busy}
                >
                  Deny
                </Button>
              </span>
            )}
          </li>
        ))}
      </ul>

      <Dialog open={denyTarget !== null} onOpenChange={(open) => !open && setDenyTarget(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Deny funds request</DialogTitle>
            <DialogDescription>
              {denyTarget
                ? `${formatCurrencyCents(denyTarget.amount)} — ${denyTarget.purpose}`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDeny}>
            <div className="space-y-2 py-2">
              <Label htmlFor="deny-note">Reason (sent to the requester)</Label>
              <Input
                id="deny-note"
                value={denyNote}
                onChange={(e) => setDenyNote(e.target.value)}
                placeholder="Needs itemized budget first"
                disabled={busy}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDenyTarget(null)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" variant="destructive" disabled={busy || !denyNote.trim()}>
                {busy ? "Denying..." : "Deny request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
